/** @jsx jsx */
import React, { useCallback, useEffect, useRef } from 'react';
import { jsx } from '@emotion/react';
import { bind } from 'bind-event-listener';
import { usePlatformLeafEventHandler } from '@atlaskit/analytics-next';
import Box from '@atlaskit/ds-explorations/box';
import noop from '@atlaskit/ds-lib/noop';
import Inline from '@atlaskit/primitives/inline';
import { useGlobalTheme } from '@atlaskit/theme/components';
import { getBgColor } from './appearances';
import { progressIndicatorGapMap, sizes, varDotsMargin, varDotsSize } from './constants';
import { ButtonIndicator, PresentationalIndicator } from './indicator';
const packageName = "@atlaskit/progress-indicator";
const packageVersion = "9.5.2";

/**
 * __ProgressDots__
 *
 * A progress indicator shows the user where they are along the steps of a journey.
 */
const ProgressDots = ({
  appearance = 'default',
  ariaControls = 'panel',
  ariaLabel = 'tab',
  size = 'default',
  // NOTE: `spacing` is a reserved HTML attribute and will be added to the
  // element, replaced with `gutter`.
  spacing: gutter = 'comfortable',
  selectedIndex,
  testId,
  values,
  onSelect
}) => {
  const tablistRef = useRef(null);
  const onSelectWithAnalytics = usePlatformLeafEventHandler({
    fn: onSelect || noop,
    action: 'selected',
    componentName: 'progressIndicator',
    packageName,
    packageVersion
  });
  const [inlineGapValue, rawGapValue] = progressIndicatorGapMap[gutter][size];
  const handleKeyDown = useCallback(event => {
    const indicators = Array.from(tablistRef.current.children);

    // bail if the target isn't an indicator
    if (!indicators.includes(event.target)) {
      return;
    }

    // bail if not valid arrow key
    const isLeft = event.key === 'ArrowLeft';
    const isRight = event.key === 'ArrowRight';
    if (!isLeft && !isRight) {
      return;
    }

    // bail if at either end of the values
    const isAlpha = isLeft && selectedIndex === 0;
    const isOmega = isRight && selectedIndex === values.length - 1;
    if (isAlpha || isOmega) {
      return;
    }
    const index = isLeft ? selectedIndex - 1 : selectedIndex + 1;

    // call the consumer's select method and focus the applicable indicator
    if (onSelect) {
      onSelectWithAnalytics({
        event: event,
        index
      });
    }
    if (typeof indicators[index].focus === 'function') {
      indicators[index].focus();
    }
  }, [onSelectWithAnalytics, selectedIndex, values, onSelect]);
  useEffect(() => {
    if (!onSelect) {
      return noop;
    }
    return bind(document, {
      type: 'keydown',
      listener: handleKeyDown,
      options: {
        capture: false
      }
    });
  }, [onSelect, handleKeyDown]);
  const theme = useGlobalTheme();
  return jsx(Box, {
    UNSAFE_style: {
      [varDotsSize]: `${sizes[size]}px`,
      [varDotsMargin]: rawGapValue
    },
    display: "block",
    role: "tablist"
  }, jsx(Inline, {
    testId: testId,
    ref: r => {
      tablistRef.current = r;
    },
    alignInline: "center",
    space: inlineGapValue
  }, values.map((_, index) => {
    const isSelected = selectedIndex === index;
    const tabId = `${ariaLabel}${index}`;
    const panelId = `${ariaControls}${index}`;
    const backgroundColor = getBgColor(appearance, isSelected)({
      theme
    });
    return onSelect ? jsx(ButtonIndicator, {
      key: index,
      style: {
        backgroundColor
      },
      "aria-controls": panelId,
      "aria-label": tabId,
      "aria-selected": isSelected,
      id: tabId,
      onClick: event => onSelectWithAnalytics({
        event,
        index
      }),
      tabIndex: isSelected ? 0 : -1,
      "data-testid": testId && `${testId}-ind-${index}`
    }) : jsx(PresentationalIndicator, {
      "data-testid": testId && `${testId}-ind-${index}`,
      key: index,
      style: {
        backgroundColor
      }
    });
  })));
};
export default ProgressDots;